import { GameController, Phase } from "./game/controller";
import { loadGame, loadSettings } from "./game/persistence";
import { createWorkerClient } from "./ai/client";
import { loadBook, setBook } from "./ai/book";
import {
  renderBoard,
  animateSubMove,
  animateDiceRoll,
  animateHitToBar,
  flashDenied,
  flashHit,
  nudgeDice,
} from "./ui/board.svg";
import { BoardLayout, DESIGN_W, VIEW_H, makeLayout } from "./ui/layout";
import {
  legalNextTargets,
  consumedDice,
  isPendingComplete,
} from "./game/controller";
import { Position } from "./engine/position";
import { Play, applyPlay } from "./engine/moves";
import { showMenu } from "./ui/menu";
import { renderPostGameReport, renderTutorCard } from "./ui/tutor-panel";
import { handleTap } from "./ui/interaction";
import { openHintModal } from "./ui/hint-modal";
import { openTutorModal } from "./ui/tutor-modal";
import { openSettingsModal } from "./ui/settings-modal";
import { openConfirmModal } from "./ui/confirm-modal";
import { closeTopModal } from "./ui/modal";
import { iconMarkup } from "./ui/icons";
import { isNativeApp } from "./platform";
import {
  unlockAudio,
  setSoundEnabled,
  playDiceRoll,
  playCheckerPlace,
  playHit,
  playDenied,
  playWin,
  playLoss,
} from "./ui/sound";

async function main(): Promise<void> {
  registerServiceWorker();
  const settings = await loadSettings();
  const ai = createWorkerClient();
  // The Worker loads the book itself; also load it here so the no-Worker
  // fallback client (which runs the engine on the main thread) is booked too.
  void loadBook("/weights/opening_book.json").then((b) => {
    if (b) setBook(b);
  }).catch(() => {});
  const controller = new GameController(settings, ai);

  const root = document.getElementById("app")!;
  buildShell(root);

  const svg = root.querySelector<SVGSVGElement>("svg.board-svg")!;
  const boardWrap = root.querySelector<HTMLElement>(".board-wrap")!;
  const overlayContainer = root.querySelector<HTMLElement>(".overlay")!;

  // Sound: honor the persisted toggle, and resume the AudioContext from the
  // first (and every) user gesture — browsers gate audio on gestures, and
  // some re-suspend the context when the tab backgrounds.
  setSoundEnabled(settings.soundEnabled);
  document.addEventListener("pointerdown", () => unlockAudio(), { capture: true });

  // Transient notices (forfeits). One element, re-triggered per message.
  const toastEl = document.createElement("div");
  toastEl.className = "toast";
  root.appendChild(toastEl);
  let toastTimer: number | undefined;
  function showToast(msg: string): void {
    toastEl.textContent = msg;
    toastEl.classList.add("show");
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toastEl.classList.remove("show"), 2400);
  }

  // "Analyzing…" pill: tutor/trainer modes hold the next roll until the move
  // analysis lands. Under ~450ms nothing shows; past that the wait gets a label
  // so it doesn't read as a hang.
  const analyzingEl = document.createElement("div");
  analyzingEl.className = "analyzing-pill";
  analyzingEl.textContent = "Analyzing…";
  root.appendChild(analyzingEl);
  let analyzingTimer: number | undefined;
  function syncAnalyzingPill(): void {
    const s = controller.state;
    // While the board waits on a manual roll it isn't computing anything —
    // the "tap to roll" cue owns that moment.
    if (s.phase.kind === "roll" && !s.awaitingRoll && s.settings.tutorMode !== "off") {
      if (analyzingTimer === undefined) {
        analyzingTimer = window.setTimeout(() => analyzingEl.classList.add("show"), 450);
      }
    } else {
      window.clearTimeout(analyzingTimer);
      analyzingTimer = undefined;
      analyzingEl.classList.remove("show");
    }
  }
  function hideAnalyzingPill(): void {
    window.clearTimeout(analyzingTimer);
    analyzingTimer = undefined;
    analyzingEl.classList.remove("show");
  }

  // Responsive layout — viewW matches the container's aspect ratio so that
  // circles always render as perfect circles (no preserveAspectRatio stretch).
  // We clamp viewW so triangles don't get absurdly wide on ultrawide displays.
  let currentLayout: BoardLayout = makeLayout(DESIGN_W, VIEW_H);
  function recomputeLayout(): boolean {
    const rect = boardWrap.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    const aspect = rect.width / rect.height;
    const viewW = Math.max(DESIGN_W, Math.min(2000, Math.round(VIEW_H * aspect)));
    if (viewW === currentLayout.viewW) return false;
    currentLayout = makeLayout(viewW, VIEW_H);
    return true;
  }
  recomputeLayout();

  // Publish the board's rect as CSS vars so `.modal-backdrop.board-anchored`
  // can centre a panel on the board rather than the window — the sidebar
  // otherwise pushes every centred modal visibly right of the board's centre.
  // Defaults live in style.css, so environments without real layout (tests)
  // fall back to the full viewport.
  function publishBoardRect(): void {
    const r = boardWrap.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    root.style.setProperty("--board-left", `${r.left}px`);
    root.style.setProperty("--board-top", `${r.top}px`);
    root.style.setProperty("--board-width", `${r.width}px`);
    root.style.setProperty("--board-height", `${r.height}px`);
  }
  publishBoardRect();

  const tutorEl = root.querySelector<HTMLElement>(".tutor-slot")!;
  const scoreEl = root.querySelector<HTMLElement>(".score")!;
  const undoBtn = root.querySelector<HTMLButtonElement>('[data-action="undo"]')!;
  const newBtn = root.querySelector<HTMLButtonElement>('[data-action="new"]')!;
  const hintBtn = root.querySelector<HTMLButtonElement>('[data-action="hint"]')!;
  const settingsBtn = root.querySelector<HTMLButtonElement>('[data-action="settings"]')!;
  const fullscreenBtn = root.querySelector<HTMLButtonElement>('[data-action="fullscreen"]')!;

  // Diagnostic tap log (dev only). Reproduces a "tap did nothing" failure by
  // reading window.__taplog after the user hits a silent failure. Each entry
  // carries enough state to identify which exit path fired.
  type TapEntry = {
    t: number;
    event: string;
    reason: string;
    clientX?: number;
    clientY?: number;
    vbX?: number;
    vbY?: number;
    targetTag?: string;
    onDice?: boolean;
    hitIdx?: number | null;
    phase: string;
    pendingLen: number;
    selectedFrom: number | null;
    dice: number[] | null;
    processing?: boolean;
    pendingLenAfter?: number;
    selectedFromAfter?: number | null;
  };
  const taplog: TapEntry[] = [];
  const TAPLOG_MAX = 50;
  function recordTap(entry: Partial<TapEntry> & { event: string; reason: string }): void {
    if (!import.meta.env?.DEV) return;
    const s = controller.state;
    const full: TapEntry = {
      t: Date.now(),
      event: entry.event,
      reason: entry.reason,
      clientX: entry.clientX,
      clientY: entry.clientY,
      vbX: entry.vbX,
      vbY: entry.vbY,
      targetTag: entry.targetTag,
      onDice: entry.onDice,
      hitIdx: entry.hitIdx,
      phase: s.phase.kind,
      pendingLen: s.pendingPlay.length,
      selectedFrom: s.selectedFrom,
      dice: s.position.dice ? [...s.position.dice] : null,
      processing: entry.processing,
      pendingLenAfter: entry.pendingLenAfter,
      selectedFromAfter: entry.selectedFromAfter,
    };
    taplog.push(full);
    if (taplog.length > TAPLOG_MAX) taplog.shift();
  }
  if (import.meta.env?.DEV) {
    (window as unknown as { __bgctrl: GameController; __taplog: TapEntry[] }).__bgctrl = controller;
    (window as unknown as { __bgctrl: GameController; __taplog: TapEntry[] }).__taplog = taplog;
  }

  // Pointer-based interaction — supports tap-to-move AND drag-to-move.
  // <6px movement after pointerdown = tap; >=6px = drag.
  type DragState = {
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startIdx: number;
    startIsSource: boolean;
    isDiceTap: boolean;
    ghost: SVGCircleElement | null;
    dragging: boolean;
  };
  let drag: DragState | null = null;
  // Drag-vs-tap threshold in client pixels. Touchscreens have natural finger
  // slop on a tap; too small a threshold turned slight slides into "cancelled
  // drag" with the user seeing nothing happen. 12px feels right on phones.
  const DRAG_THRESHOLD = 12;

  svg.addEventListener("pointerdown", (ev) => {
    // Idle board (no game in progress): a tap anywhere starts a new game with
    // the current settings. The 🎲 button still opens the New Game dialog.
    if (controller.state.phase.kind === "menu") {
      recordTap({ event: "pointerdown", reason: "menu-tap-start", clientX: ev.clientX, clientY: ev.clientY, targetTag: (ev.target as Element | null)?.tagName });
      controller.startNewMatch();
      attemptOrientationLock();
      return;
    }
    // Auto-roll off: the board is parked waiting for this player to roll, and
    // the whole board is the button (matching the "tap anywhere to roll" cue).
    if (controller.state.awaitingRoll) {
      recordTap({ event: "pointerdown", reason: "manual-roll", clientX: ev.clientX, clientY: ev.clientY, targetTag: (ev.target as Element | null)?.tagName });
      controller.rollDice();
      return;
    }
    if (controller.state.phase.kind !== "play") {
      recordTap({ event: "pointerdown", reason: "wrong-phase", clientX: ev.clientX, clientY: ev.clientY, targetTag: (ev.target as Element | null)?.tagName });
      return;
    }
    const target = ev.target as Element | null;
    const onDice = !!(target && target.closest('[data-dice-zone]'));
    const pt = svgPoint(svg, ev.clientX, ev.clientY);
    if (!pt) {
      recordTap({ event: "pointerdown", reason: "svgPoint-null", clientX: ev.clientX, clientY: ev.clientY, targetTag: target?.tagName });
      return;
    }
    const idx = onDice ? null : currentLayout.hitTest(pt.x, pt.y, controller.shouldFlipDisplay());
    if (!onDice && idx === null) {
      recordTap({ event: "pointerdown", reason: "hittest-null", clientX: ev.clientX, clientY: ev.clientY, vbX: pt.x, vbY: pt.y, targetTag: target?.tagName, onDice, hitIdx: idx });
      return;
    }
    const phase = controller.state.phase as Extract<Phase, { kind: "play" }>;
    const targets = legalNextTargets(phase.legalPlays, controller.state.pendingPlay);
    try {
      svg.setPointerCapture(ev.pointerId);
    } catch {
      // pointer capture not supported — fall back to delegated events
    }
    drag = {
      pointerId: ev.pointerId,
      startClientX: ev.clientX,
      startClientY: ev.clientY,
      startIdx: idx ?? -2,
      startIsSource: idx !== null && targets.has(idx),
      isDiceTap: onDice,
      ghost: null,
      dragging: false,
    };
    recordTap({ event: "pointerdown", reason: onDice ? "captured-dice" : drag.startIsSource ? "captured-source" : "captured-non-source", clientX: ev.clientX, clientY: ev.clientY, vbX: pt.x, vbY: pt.y, targetTag: target?.tagName, onDice, hitIdx: idx });
  });

  svg.addEventListener("pointermove", (ev) => {
    if (!drag || ev.pointerId !== drag.pointerId) return;
    if (!drag.startIsSource) return;
    const dx = ev.clientX - drag.startClientX;
    const dy = ev.clientY - drag.startClientY;
    if (!drag.dragging) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      drag.dragging = true;
      controller.pickFrom(drag.startIdx);
      const ourColor: "white" | "black" = controller.state.position.turn === 0 ? "white" : "black";
      const pt = svgPoint(svg, ev.clientX, ev.clientY);
      if (pt) drag.ghost = createDragGhost(svg, pt.x, pt.y, ourColor);
    } else if (drag.ghost) {
      const pt = svgPoint(svg, ev.clientX, ev.clientY);
      if (pt) {
        drag.ghost.setAttribute("cx", String(pt.x));
        drag.ghost.setAttribute("cy", String(pt.y));
      }
    }
  });

  function finishInteraction(ev: PointerEvent, cancelled: boolean): void {
    if (!drag || ev.pointerId !== drag.pointerId) return;
    const d = drag;
    drag = null;
    try {
      svg.releasePointerCapture(ev.pointerId);
    } catch {
      // ignore
    }
    if (d.ghost) d.ghost.remove();
    const pendingBefore = controller.state.pendingPlay.length;
    const selectedBefore = controller.state.selectedFrom;
    if (cancelled) {
      if (d.dragging) controller.clearSelection();
      recordTap({ event: "pointercancel", reason: d.dragging ? "drag-cancelled" : "tap-cancelled", clientX: ev.clientX, clientY: ev.clientY, hitIdx: d.startIdx, processing });
      return;
    }
    if (d.isDiceTap && !d.dragging) {
      const diceBefore = controller.state.position.dice?.join(",") ?? "";
      const phaseBefore = controller.state.phase.kind;
      handleDiceTap();
      const after = controller.state;
      // Neither a commit (phase change) nor a swap (dice reorder) happened —
      // the tap bounced. Say so instead of doing nothing.
      if (
        phaseBefore === "play"
        && after.phase.kind === "play"
        && after.pendingPlay.length === pendingBefore
        && (after.position.dice?.join(",") ?? "") === diceBefore
      ) {
        nudgeDice(svg);
        playDenied();
      }
      recordTap({ event: "pointerup", reason: "dice-tap", clientX: ev.clientX, clientY: ev.clientY, onDice: true, processing, pendingLenAfter: after.pendingPlay.length, selectedFromAfter: after.selectedFrom });
      return;
    }
    const pt = svgPoint(svg, ev.clientX, ev.clientY);
    if (!d.dragging) {
      // Tap path
      const idx = pt ? currentLayout.hitTest(pt.x, pt.y, controller.shouldFlipDisplay()) : null;
      if (idx !== null) {
        handleTap(controller, idx);
        const after = controller.state;
        const moved = after.pendingPlay.length !== pendingBefore || after.selectedFrom !== null;
        // The tap changed nothing at all (no move, no selection change) —
        // flash the zone so the rejection is visible, not silent.
        const acted = after.pendingPlay.length !== pendingBefore || after.selectedFrom !== selectedBefore;
        if (!acted && after.phase.kind === "play") {
          flashDenied(svg, currentLayout, idx, controller.shouldFlipDisplay());
          playDenied();
        }
        recordTap({ event: "pointerup", reason: moved ? "tap-moved" : "tap-noop", clientX: ev.clientX, clientY: ev.clientY, vbX: pt?.x, vbY: pt?.y, hitIdx: idx, processing, pendingLenAfter: after.pendingPlay.length, selectedFromAfter: after.selectedFrom });
      } else {
        recordTap({ event: "pointerup", reason: "tap-hittest-null", clientX: ev.clientX, clientY: ev.clientY, vbX: pt?.x, vbY: pt?.y, hitIdx: idx, processing });
      }
      return;
    }
    // Drag path: drop only if drop target is a legal destination of the source.
    const dropIdx = pt ? currentLayout.hitTest(pt.x, pt.y, controller.shouldFlipDisplay()) : null;
    // If the drag ended on the same idx it started (finger slop on a tap that
    // happened to cross the threshold), treat it as a tap so the user gets a
    // result instead of a silent cancel.
    if (dropIdx === d.startIdx) {
      handleTap(controller, d.startIdx);
      const after = controller.state;
      const moved = after.pendingPlay.length !== pendingBefore || after.selectedFrom !== null;
      recordTap({ event: "pointerup", reason: moved ? "drag-tap-moved" : "drag-tap-noop", clientX: ev.clientX, clientY: ev.clientY, hitIdx: d.startIdx, processing, pendingLenAfter: after.pendingPlay.length });
      return;
    }
    if (dropIdx === null) {
      controller.clearSelection();
      recordTap({ event: "pointerup", reason: "drag-drop-off-board", clientX: ev.clientX, clientY: ev.clientY, hitIdx: dropIdx, processing });
      return;
    }
    const phase = controller.state.phase;
    if (phase.kind !== "play") {
      recordTap({ event: "pointerup", reason: "drag-wrong-phase", clientX: ev.clientX, clientY: ev.clientY, hitIdx: dropIdx, processing });
      return;
    }
    const targets = legalNextTargets(phase.legalPlays, controller.state.pendingPlay);
    const dests = targets.get(d.startIdx);
    if (dests && dests.has(dropIdx)) {
      controller.pickTo(dropIdx);
      recordTap({ event: "pointerup", reason: "drag-moved", clientX: ev.clientX, clientY: ev.clientY, hitIdx: dropIdx, processing, pendingLenAfter: controller.state.pendingPlay.length });
    } else {
      controller.clearSelection();
      flashDenied(svg, currentLayout, dropIdx, controller.shouldFlipDisplay());
      playDenied();
      recordTap({ event: "pointerup", reason: "drag-drop-not-dest", clientX: ev.clientX, clientY: ev.clientY, hitIdx: dropIdx, processing });
    }
  }

  function handleDiceTap(): void {
    const s = controller.state;
    if (s.phase.kind !== "play") return;
    // Pending complete -> confirm/commit.
    const complete = isPendingComplete(s.phase.legalPlays, s.pendingPlay);
    if (complete) {
      controller.commitPlay();
      return;
    }
    // Pending empty + two non-double dice -> swap order.
    if (s.pendingPlay.length === 0 && s.position.dice && s.position.dice.length === 2 && s.position.dice[0] !== s.position.dice[1]) {
      controller.swapDice();
    }
  }

  svg.addEventListener("pointerup", (ev) => finishInteraction(ev, false));
  svg.addEventListener("pointercancel", (ev) => finishInteraction(ev, true));
  // Prevent the synthetic mouse-click that browsers fire after a touchend from
  // re-dispatching tap logic — our pointer handlers already covered it.
  svg.addEventListener("click", (ev) => ev.preventDefault());

  // Ephemeral hint preview: set by the hint modal to overlay arrows on the
  // board, cleared when the modal closes (or the user dismisses it). Cleared
  // on every controller emit too — moving the pieces invalidates the preview.
  let previewHintMoves: { from: number; to: number }[] | null = null;
  // Tutor preview: while the error/blunder analysis modal is open we paint
  // the board in its pre-move state so the user can see arrows referencing
  // the position they actually played from. Cleared on modal close.
  let tutorPreview: { startPos: Position; flipped: boolean; ourColor: "white" | "black" } | null = null;

  function repaintWithOverrides(): void {
    if (tutorPreview) {
      const snap: BoardSnapshot = {
        position: tutorPreview.startPos,
        pendingPlay: [],
        selectedFrom: null,
        phase: { kind: "play", legalPlays: [] },
        flipped: tutorPreview.flipped,
        ourColor: tutorPreview.ourColor,
        gameNumber: controller.state.gameNumber,
        awaitingRoll: false,
      };
      paintBoardFrame(tutorPreview.startPos, snap, { hideTopFrom: null, pendingPlayLen: 0 });
      return;
    }
    if (lastShown) {
      paintBoardFrame(workingFromSnap(lastShown), lastShown, {
        hideTopFrom: null,
        pendingPlayLen: lastShown.pendingPlay.length,
      });
    }
  }

  function setPreviewHint(moves: { from: number; to: number }[] | null): void {
    previewHintMoves = moves;
    repaintWithOverrides();
  }

  function computeFlippedFor(turn: 0 | 1): boolean {
    const s = controller.state.settings;
    if (s.whitePlayer === "cpu" && s.blackPlayer === "cpu") return false;
    if (s.whitePlayer === "human" && s.blackPlayer === "human") {
      return turn === 1;
    }
    const humanSide = s.whitePlayer === "human" ? 0 : 1;
    return turn !== humanSide;
  }

  function setTutorPreview(
    pre: { startPos: Position; side: 0 | 1; hintMoves: { from: number; to: number }[] | null } | null,
  ): void {
    if (pre === null) {
      tutorPreview = null;
      previewHintMoves = null;
      repaintWithOverrides();
      return;
    }
    tutorPreview = {
      startPos: pre.startPos,
      flipped: computeFlippedFor(pre.side),
      ourColor: pre.side === 0 ? "white" : "black",
    };
    previewHintMoves = pre.hintMoves;
    repaintWithOverrides();
  }

  undoBtn.addEventListener("click", () => controller.undo());
  newBtn.addEventListener("click", () => {
    // Mid-game the button is a flag: resigning ends the game properly (the
    // opponent scores it) and lands on the win screen with the play-rating
    // report, instead of silently discarding the game and everything you'd
    // have learned from it. "New game…" is still one tap further in.
    if (!isInGame(controller)) {
      openMenu(controller, root);
      return;
    }
    const s = controller.state.settings;
    const winnerName = controller.resigningSide() === 0 ? s.blackName : s.whiteName;
    const rated = s.tutorMode !== "off" && controller.state.tutor.history.length > 0;
    openConfirmModal(overlayContainer, {
      title: "Resign this game?",
      body:
        `${winnerName} wins the game.`
        + (rated ? " You'll get your rating for the moves you played." : ""),
      confirmLabel: "Resign",
      danger: true,
      onConfirm: () => controller.resign(),
      secondaryLabel: "New game…",
      onSecondary: () => openMenu(controller, root),
    });
  });
  hintBtn.addEventListener("click", () => void openHintModal(controller, overlayContainer, { setPreviewHint }));
  settingsBtn.addEventListener("click", () => openSettingsModal(controller, overlayContainer));
  if (isNativeApp()) {
    // The Android shell is already immersive (Capacitor SystemBars hides both
    // bars), and requestFullscreen() silently no-ops in a WebView with no
    // onShowCustomView host. A dead control is worse than no control.
    fullscreenBtn.remove();
  } else {
    fullscreenBtn.addEventListener("click", () => {
      if (document.fullscreenElement) {
        void document.exitFullscreen().catch(() => {});
      } else {
        void document.documentElement.requestFullscreen().catch(() => {});
      }
    });
    const updateFullscreenIcon = (): void => {
      fullscreenBtn.title = document.fullscreenElement ? "Exit fullscreen" : "Fullscreen";
      fullscreenBtn.setAttribute("aria-label", fullscreenBtn.title);
    };
    document.addEventListener("fullscreenchange", updateFullscreenIcon);
  }

  // ---- Board animation pipeline ----
  // Chrome (topbar/buttons/overlays/tutor) updates synchronously on every emit.
  // The board renders through an async queue so we can play a ~150ms slide for
  // each sub-move (CPU plays multiple at once; humans appear one-by-one).

  type BoardSnapshot = {
    position: Position;
    pendingPlay: Play;
    selectedFrom: number | null;
    phase: Phase;
    flipped: boolean;
    ourColor: "white" | "black";
    gameNumber: number;
    awaitingRoll: boolean;
  };

  const snapshotQueue: BoardSnapshot[] = [];
  let lastShown: BoardSnapshot | null = null;
  let processing = false;

  function snapshotBoard(): BoardSnapshot {
    const s = controller.state;
    return {
      position: s.position,
      pendingPlay: [...s.pendingPlay],
      selectedFrom: s.selectedFrom,
      phase: s.phase,
      flipped: controller.shouldFlipDisplay(),
      ourColor: s.position.turn === 0 ? "white" : "black",
      gameNumber: s.gameNumber,
      awaitingRoll: s.awaitingRoll,
    };
  }

  // Identity of the visible roll — used to detect a FRESH roll (which gets the
  // tumble animation + sound) vs repaints of the same roll. Dice order is
  // normalized out so a mid-turn swap doesn't re-trigger the animation.
  function diceKeyOf(s: BoardSnapshot | null): string | null {
    if (!s || !s.position.dice || s.position.dice.length === 0) return null;
    const k = s.phase.kind;
    if (k !== "play" && k !== "cpu-thinking" && k !== "opening") return null;
    return `${[...s.position.dice].sort().join("")}|${s.position.turn}|${s.gameNumber}`;
  }

  // The dice faces renderBoard will show (doubles display as four dice).
  function displayDiceValues(s: BoardSnapshot): number[] {
    const d = s.position.dice;
    if (!d || d.length !== 2) return d ?? [];
    return d[0] === d[1] ? [d[0], d[0], d[0], d[0]] : [d[0], d[1]];
  }

  // Tutor-modal de-dup: only pop a modal the first time a given tutor entry
  // is observed. We compare history length so emits that don't add a new
  // entry (board re-paints, etc.) don't re-open the modal.
  let lastTutorHistoryLen = 0;
  // Forfeit notice de-dup: the forfeited "play" phase object is stable across
  // emits, so a WeakSet keys the notice to the specific occurrence.
  const announcedForfeits = new WeakSet<object>();
  controller.subscribe(() => {
    // Any state change invalidates an in-flight hint preview — clear it so
    // arrows don't linger over a position they no longer describe.
    previewHintMoves = null;
    const s = controller.state;
    setSoundEnabled(s.settings.soundEnabled);
    renderChrome();
    snapshotQueue.push(snapshotBoard());
    if (!processing) void processQueue();
    syncAnalyzingPill();
    // A roll with no legal moves silently evaporated before — announce it.
    if (
      s.phase.kind === "play"
      && s.phase.legalPlays.length === 1
      && s.phase.legalPlays[0].length === 0
      && !announcedForfeits.has(s.phase)
    ) {
      announcedForfeits.add(s.phase);
      const name = s.position.turn === 0 ? s.settings.whiteName : s.settings.blackName;
      showToast(
        winnerIsSecondPerson(name)
          ? "No legal moves — your turn is skipped"
          : `${name} has no legal moves — turn skipped`,
      );
    }
    // If a new tutor entry was just recorded and it's an error/blunder,
    // surface the analysis modal so the user can learn from it.
    if (s.tutor.history.length > lastTutorHistoryLen) {
      const newest = s.tutor.history[s.tutor.history.length - 1];
      lastTutorHistoryLen = s.tutor.history.length;
      // Only "tutor" mode surfaces per-turn flags; "trainer" stays silent until
      // the end-of-game report.
      if (
        s.settings.tutorMode === "tutor" &&
        (newest.classification === "error" || newest.classification === "blunder")
      ) {
        hideAnalyzingPill();
        openTutorModal(newest, overlayContainer, {
          setTutorPreview,
          onDismiss: () => controller.ackTutor(),
        });
      }
    }
  });

  async function processQueue(): Promise<void> {
    processing = true;
    try {
      while (snapshotQueue.length > 0) {
        const next = snapshotQueue.shift()!;
        await transitionBoard(next);
        lastShown = next;
      }
    } finally {
      processing = false;
    }
  }

  async function transitionBoard(next: BoardSnapshot): Promise<void> {
    // The tutor preview owns the canvas while it's showing (post-error/blunder
    // modal). Skip painting queued snapshots — otherwise the post-mirror
    // snapshot pushed by the commit's emit() lands on top of the pre-move
    // preview, leaving the arrows pointing at the opponent's pieces.
    if (tutorPreview) return;
    // A fresh roll: tumble the dice in (with sound) before settling on the
    // real faces. Fresh-roll snapshots always carry an empty pendingPlay, so
    // this never overlaps the sub-move branch below.
    if (diceKeyOf(next) !== null && diceKeyOf(lastShown) !== diceKeyOf(next)) {
      paintBoardFrame(workingFromSnap(next), next, {
        hideTopFrom: null,
        pendingPlayLen: next.pendingPlay.length,
        hideDice: true,
      });
      playDiceRoll();
      await animateDiceRoll(svg, currentLayout, next.ourColor, displayDiceValues(next));
    }
    // Animate the new sub-moves if (a) we share a position with the last frame
    // and (b) `next.pendingPlay` is an extension of `lastShown.pendingPlay`.
    if (
      lastShown &&
      samePosition(lastShown.position, next.position) &&
      isAppend(lastShown.pendingPlay, next.pendingPlay)
    ) {
      for (let i = lastShown.pendingPlay.length; i < next.pendingPlay.length; i++) {
        const before = applyPlay(next.position, next.pendingPlay.slice(0, i));
        const sub = next.pendingPlay[i];
        const isHit = sub.to >= 0 && sub.to < 24 && before.points[sub.to] === -1;
        paintBoardFrame(before, next, {
          hideTopFrom: sub.from,
          pendingPlayLen: i,
        });
        await animateSubMove(svg, before, sub, next.flipped, next.ourColor, currentLayout, 150);
        if (isHit) {
          // The landing knocks the blot to the bar: ring + sting, then the
          // blot visibly travels. The intermediate frame has our checker
          // landed and the bar stack one short while the ghost flies.
          playHit();
          flashHit(svg, currentLayout, sub.to, next.flipped);
          const after = applyPlay(next.position, next.pendingPlay.slice(0, i + 1));
          paintBoardFrame(after, next, {
            hideTopFrom: null,
            pendingPlayLen: i + 1,
            hideBarThemTop: true,
          });
          await animateHitToBar(svg, before, sub.to, next.flipped, next.ourColor, currentLayout);
        } else {
          playCheckerPlace();
        }
      }
    }
    paintBoardFrame(workingFromSnap(next), next, {
      hideTopFrom: null,
      pendingPlayLen: next.pendingPlay.length,
    });
  }

  function workingFromSnap(s: BoardSnapshot): Position {
    if (s.pendingPlay.length === 0) return s.position;
    return applyPlay(s.position, s.pendingPlay);
  }

  function paintBoardFrame(
    pos: Position,
    s: BoardSnapshot,
    opts: {
      hideTopFrom: number | null;
      pendingPlayLen: number;
      hideDice?: boolean;
      hideBarThemTop?: boolean;
    },
  ): void {
    const phase = s.phase;
    const dice = s.position.dice ?? [];
    // Build the dice display array: rolled order is preserved across the turn,
    // each slot independently marked used/unused. This keeps the left die in
    // the left position even after it's consumed (it fades in place).
    let diceDisplay: { d: number; used: boolean }[] = [];
    if (dice.length === 2) {
      const rolled = dice[0] === dice[1] ? [dice[0], dice[0], dice[0], dice[0]] : [dice[0], dice[1]];
      if (phase.kind === "play") {
        const consumedPrefix = s.pendingPlay.slice(0, opts.pendingPlayLen);
        const consumed = consumedDice(consumedPrefix);
        const counts: Record<number, number> = {};
        for (const c of consumed) counts[c] = (counts[c] ?? 0) + 1;
        diceDisplay = rolled.map((d) => {
          if ((counts[d] ?? 0) > 0) {
            counts[d] = (counts[d] as number) - 1;
            return { d, used: true };
          }
          return { d, used: false };
        });
        // If no further sub-move is possible from this prefix (i.e., the
        // turn is over even though some dice were never consumed — common
        // when only one die can legally be played), mark those leftover
        // dice as "used" too. Visually signals "your turn is done".
        if (isPendingComplete(phase.legalPlays, consumedPrefix)) {
          diceDisplay = diceDisplay.map((d) => (d.used ? d : { d: d.d, used: true }));
        }
      } else if (phase.kind === "opening" || phase.kind === "cpu-thinking" || phase.kind === "roll") {
        diceDisplay = rolled.map((d) => ({ d, used: false }));
      }
    }
    // The source wash (faint highlight on tappable points) is driven by the
    // KEYS of legalDestsFrom — we always compute it from the CURRENT pending
    // prefix so it matches the visible board state. Suppressing it during
    // intermediate animation frames would make the wash blink off and on for
    // ~150 ms each sub-move, which reads as point-color flashing.
    const targets =
      phase.kind === "play"
        ? legalNextTargets(phase.legalPlays, s.pendingPlay.slice(0, opts.pendingPlayLen))
        : new Map<number, Set<number>>();
    // The diceCue (commit/swap tap-zones) is only meaningful on the FINAL
    // settled frame — never during animation — so we gate it by checking
    // whether we're rendering the full pending play.
    const isFinalFrame = opts.pendingPlayLen === s.pendingPlay.length;
    let diceCue: "swap" | "confirm" | null = null;
    if (phase.kind === "play" && isFinalFrame) {
      if (isPendingComplete(phase.legalPlays, s.pendingPlay)) diceCue = "confirm";
      else if (
        s.pendingPlay.length === 0 &&
        s.position.dice &&
        s.position.dice.length === 2 &&
        s.position.dice[0] !== s.position.dice[1]
      ) {
        diceCue = "swap";
      }
    }
    // Pip counts from the canonical (us-perspective) position currently
    // being rendered. Only emitted when the user opted in via settings.
    const pipCount = controller.state.settings.showPipCount ? computePipsUs(pos) : null;
    // Equity (absolute white POV) representing the game state after the LAST
    // committed move. Shown in the wood divider bar between the trays.
    const equity = controller.state.settings.showEquity ? controller.state.currentEquity : null;
    renderBoard(svg, pos, {
      layout: currentLayout,
      flipped: s.flipped,
      // Selection ring is hidden during animation because the source's top
      // checker is itself hidden (hideTopFrom). On the final frame, selectedFrom
      // is naturally null (pickTo cleared it) so this just shows nothing.
      selectedFrom: isFinalFrame ? s.selectedFrom : null,
      legalDestsFrom: targets,
      dice: diceDisplay,
      ourColor: s.ourColor,
      hideTopFrom: opts.hideTopFrom,
      diceCue,
      cpuThinking: phase.kind === "cpu-thinking",
      hintMoves: previewHintMoves ?? undefined,
      pipCount,
      equity,
      idleCta: phase.kind === "menu",
      rollCta: s.awaitingRoll,
      hideDice: opts.hideDice,
      hideBarThemTop: opts.hideBarThemTop,
    });
  }

  // Guard: phase overlays (won/match-won) are only torn down and rebuilt when
  // this key changes. Without it, every unrelated emit (equity updates,
  // settings changes) would remove and re-append the overlay — replaying its
  // entrance animation and win sound each time.
  let lastOverlayKey = "";
  // The end-of-game report's slot, kept so a tutor entry that lands after the
  // overlay was built (the winning move is analyzed asynchronously) updates the
  // rating in place instead of being dropped from it.
  let reportSlot: { el: HTMLElement; historyLen: number } | null = null;

  function renderChrome(): void {
    const s = controller.state;
    const phase = s.phase;
    const wName = s.settings.whiteName;
    const bName = s.settings.blackName;
    const activeSide =
      phase.kind === "play" || phase.kind === "cpu-thinking" || phase.kind === "roll" || phase.kind === "opening"
        ? s.position.turn
        : null;
    scoreEl.innerHTML =
      `<span class="pl${activeSide === 0 ? " active" : ""}">${escapeHtml(wName)} ${s.whiteScore}</span>`
      + `<span class="dash">—</span>`
      + `<span class="pl${activeSide === 1 ? " active" : ""}">${s.blackScore} ${escapeHtml(bName)}</span>`;
    undoBtn.disabled = !(phase.kind === "play" && s.pendingPlay.length > 0);
    hintBtn.disabled = !(phase.kind === "play");

    // New-game button doubles as resign mid-game and "start a game" when
    // no game is in progress. Swap the icon/tooltip so the affordance matches.
    const inGame = isInGame(controller);
    const newIcon = newBtn.querySelector<HTMLElement>(".icon")!;
    if (inGame) {
      newIcon.innerHTML = iconMarkup("flag");
      newBtn.title = "Resign game";
      newBtn.setAttribute("aria-label", "Resign game");
    } else {
      newIcon.innerHTML = iconMarkup("dice");
      newBtn.title = "New game";
      newBtn.setAttribute("aria-label", "New game");
    }

    // Sidebar tutor chip: in tutor mode, a passive marker of the last move's
    // verdict (notably "doubtful", which never pops the modal).
    if (s.settings.tutorMode === "tutor") {
      renderTutorCard(tutorEl, s.tutor.lastEntry);
    } else {
      tutorEl.innerHTML = "";
    }

    if (reportSlot && document.contains(reportSlot.el) && reportSlot.historyLen !== s.tutor.history.length) {
      reportSlot.historyLen = s.tutor.history.length;
      renderPostGameReport(reportSlot.el, s.tutor.history, wName, bName);
    }

    const overlayKey = `${phase.kind}|${s.whiteScore}|${s.blackScore}|${s.gameNumber}|${s.settings.tutorMode}`;
    if (overlayKey === lastOverlayKey) return;
    lastOverlayKey = overlayKey;

    // Clear only phase-driven overlays (won, match-won). Leave user-triggered
    // modals (hint, tutor analysis) alone — they own their own lifecycle and
    // must not be wiped by an unrelated state emit.
    for (const node of Array.from(overlayContainer.querySelectorAll(".phase-overlay"))) {
      node.remove();
    }
    reportSlot = null;
    if (phase.kind === "won" || phase.kind === "match-won") {
      const winnerSide = phase.kind === "won" ? phase.result.winner : phase.winner;
      const humanWon =
        (winnerSide === 0 ? s.settings.whitePlayer : s.settings.blackPlayer) === "human";
      // Let the final board state land before the ceremony starts (the CSS
      // entrance animation is delayed to match). Re-check the phase at fire
      // time — the player may have already started the next game.
      setTimeout(() => {
        const k = controller.state.phase.kind;
        if (k !== "won" && k !== "match-won") return;
        if (humanWon) playWin();
        else playLoss();
      }, 450);
    }
    if (phase.kind === "won") {
      const div = document.createElement("div");
      div.className = "modal-backdrop phase-overlay celebrate";
      const m = document.createElement("div");
      m.className = "modal";
      const winnerName = phase.result.winner === 0 ? wName : bName;
      const verb = winnerIsSecondPerson(winnerName) ? "win" : "wins";
      const kind =
        phase.result.kind === "single"
          ? verb
          : phase.result.kind === "gammon"
            ? `${verb} a gammon`
            : `${verb} a backgammon`;
      m.innerHTML = `<h2>${escapeHtml(winnerName)} ${kind}!</h2>`;
      if (s.settings.tutorMode !== "off" && s.tutor.history.length > 0) {
        const slot = document.createElement("div");
        renderPostGameReport(slot, s.tutor.history, wName, bName);
        m.appendChild(slot);
        reportSlot = { el: slot, historyLen: s.tutor.history.length };
      }
      const actions = document.createElement("div");
      actions.className = "actions";
      const matchOver =
        s.settings.matchLength > 1 && (s.whiteScore >= s.settings.matchLength || s.blackScore >= s.settings.matchLength);
      if (!matchOver) {
        const next = document.createElement("button");
        next.className = "primary";
        next.textContent = "Next game";
        next.addEventListener("click", () => controller.startNewGame());
        actions.appendChild(next);
      }
      const menu = document.createElement("button");
      menu.textContent = "Main menu";
      menu.addEventListener("click", () => openMenu(controller, root));
      actions.appendChild(menu);
      m.appendChild(actions);
      div.appendChild(m);
      overlayContainer.appendChild(div);
      m.querySelector<HTMLButtonElement>("button.primary, button")?.focus();
    } else if (phase.kind === "match-won") {
      const div = document.createElement("div");
      div.className = "modal-backdrop phase-overlay celebrate";
      const m = document.createElement("div");
      m.className = "modal";
      const winnerName = phase.winner === 0 ? wName : bName;
      const matchVerb = winnerIsSecondPerson(winnerName) ? "win" : "wins";
      m.innerHTML = `<h2>${escapeHtml(winnerName)} ${matchVerb} the match!</h2>
        <p>${escapeHtml(wName)} ${s.whiteScore} — ${s.blackScore} ${escapeHtml(bName)}</p>`;
      if (s.settings.tutorMode !== "off" && s.tutor.history.length > 0) {
        const slot = document.createElement("div");
        renderPostGameReport(slot, s.tutor.history, wName, bName);
        m.appendChild(slot);
        reportSlot = { el: slot, historyLen: s.tutor.history.length };
      }
      const actions = document.createElement("div");
      actions.className = "actions";
      const menu = document.createElement("button");
      menu.className = "primary";
      menu.textContent = "Main menu";
      menu.addEventListener("click", () => openMenu(controller, root));
      actions.appendChild(menu);
      m.appendChild(actions);
      div.appendChild(m);
      overlayContainer.appendChild(div);
      m.querySelector<HTMLButtonElement>("button.primary")?.focus();
    }
  }

  // Initial paint
  recomputeLayout();
  const initSnap = snapshotBoard();
  renderChrome();
  paintBoardFrame(workingFromSnap(initSnap), initSnap, {
    hideTopFrom: null,
    pendingPlayLen: initSnap.pendingPlay.length,
  });
  lastShown = initSnap;

  // Re-render on container resize so the viewBox tracks the window aspect.
  // We only re-render when the computed viewW actually changes (round-trip
  // through `recomputeLayout`'s no-op guard) to avoid wasted work.
  const ro = new ResizeObserver(() => {
    // Before the viewW guard below: the rect can move without viewW changing
    // (topbar hiding at the landscape breakpoint, fullscreen, safe-area
    // shifts), and a board-anchored modal has to follow it.
    publishBoardRect();
    if (!recomputeLayout()) return;
    if (!lastShown) return;
    paintBoardFrame(workingFromSnap(lastShown), lastShown, {
      hideTopFrom: null,
      pendingPlayLen: lastShown.pendingPlay.length,
    });
  });
  ro.observe(boardWrap);

  installAndroidBackButton(overlayContainer, controller);

  // Resume a game in progress (checkpointed at turn boundaries). Invalid or
  // absent blobs fall through to the idle menu board painted above.
  const savedBlob = await loadGame();
  if (savedBlob) controller.resumeGame(savedBlob);
}

// Pip count from a canonical (us-coords) position. Renderer uses these
// directly with the {us, them} convention.
function computePipsUs(p: Position): { us: number; them: number } {
  let us = p.barUs * 25;
  let them = p.barThem * 25;
  for (let i = 0; i < 24; i++) {
    const v = p.points[i];
    if (v > 0) us += v * (i + 1);
    else if (v < 0) them += -v * (24 - i);
  }
  return { us, them };
}

function samePosition(a: Position, b: Position): boolean {
  if (a.turn !== b.turn) return false;
  if (a.barUs !== b.barUs || a.barThem !== b.barThem) return false;
  if (a.offUs !== b.offUs || a.offThem !== b.offThem) return false;
  for (let i = 0; i < 24; i++) if (a.points[i] !== b.points[i]) return false;
  return true;
}

function isAppend(prev: Play, next: Play): boolean {
  if (next.length <= prev.length) return false;
  for (let i = 0; i < prev.length; i++) {
    if (prev[i].from !== next[i].from || prev[i].to !== next[i].to || prev[i].die !== next[i].die) return false;
  }
  return true;
}

function buildShell(root: HTMLElement): void {
  root.innerHTML = `
    <div class="rotate-prompt">
      <div class="rotate-title">Backgammon</div>
      <div class="rotate-icon">↻</div>
      <div>Rotate your device to landscape</div>
    </div>
    <div class="topbar">
      <div class="title">Backgammon</div>
      <div class="score">—</div>
    </div>
    <div class="main">
      <div class="board-wrap">
        <svg class="board-svg" xmlns="http://www.w3.org/2000/svg"></svg>
      </div>
      <aside class="sidebar">
        <div class="actions actions-top">
          <button data-action="undo" title="Undo" aria-label="Undo"><span class="label">Undo</span><span class="icon" aria-hidden="true">${iconMarkup("undo")}</span></button>
          <button data-action="hint" title="Hint" aria-label="Hint"><span class="label">Hint</span><span class="icon" aria-hidden="true">${iconMarkup("hint")}</span></button>
          <button data-action="new" title="New game (surrender)" aria-label="New game"><span class="label">New game</span><span class="icon" aria-hidden="true">${iconMarkup("dice")}</span></button>
        </div>
        <div class="tutor-slot"></div>
        <div class="actions actions-bottom">
          <button data-action="fullscreen" title="Fullscreen" aria-label="Fullscreen"><span class="label">Fullscreen</span><span class="icon" aria-hidden="true">${iconMarkup("fullscreen")}</span></button>
          <button data-action="settings" title="Settings" aria-label="Settings"><span class="label">Settings</span><span class="icon" aria-hidden="true">${iconMarkup("settings")}</span></button>
        </div>
      </aside>
    </div>
    <div class="overlay"></div>
  `;
}

function attemptOrientationLock(): void {
  // The Android app pins landscape via the activity's screenOrientation; the
  // web API rejects inside a WebView anyway.
  if (isNativeApp()) return;
  type LockableOrientation = ScreenOrientation & { lock?: (o: string) => Promise<void> };
  const so = screen.orientation as LockableOrientation | undefined;
  if (so && typeof so.lock === "function") {
    so.lock("landscape").catch(() => {
      // Lock often requires fullscreen; silently ignore failures.
    });
  }
}

/**
 * Android hardware/gesture back. Capacitor's default is "go back in history,
 * and exit when there is none" — and this SPA pushes no history entries, so
 * without this the first back press kills the app mid-game.
 *
 * Precedence: dismiss an open dialog, else confirm before abandoning a game in
 * progress, else exit.
 */
function installAndroidBackButton(
  overlayContainer: HTMLElement,
  controller: GameController,
): void {
  if (!isNativeApp()) return;
  // Dynamic import so the browser bundle never pulls in @capacitor/app.
  void import("@capacitor/app")
    .then(({ App }) => {
      void App.addListener("backButton", () => {
        if (closeTopModal()) return;
        const kind = controller.state.phase.kind;
        if (kind === "menu" || kind === "won" || kind === "match-won") {
          void App.exitApp();
          return;
        }
        openConfirmModal(overlayContainer, {
          title: "Quit Backgammon?",
          body: "Your game is saved and will resume the next time you open the app.",
          confirmLabel: "Quit",
          danger: true,
          onConfirm: () => void App.exitApp(),
        });
      });
    })
    .catch(() => {
      // Plugin unavailable: leave Capacitor's default back handling in place.
    });
}

function createDragGhost(svg: SVGSVGElement, cx: number, cy: number, ourColor: "white" | "black"): SVGCircleElement {
  const fill = ourColor === "white" ? "var(--checker-w)" : "var(--checker-b)";
  const stroke = ourColor === "white" ? "var(--checker-w-edge)" : "var(--checker-b-edge)";
  const c = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  c.setAttribute("cx", String(cx));
  c.setAttribute("cy", String(cy));
  c.setAttribute("r", "24");
  c.setAttribute("fill", fill);
  c.setAttribute("stroke", stroke);
  c.setAttribute("stroke-width", "2");
  c.setAttribute("opacity", "0.85");
  c.setAttribute("pointer-events", "none");
  c.setAttribute("filter", "drop-shadow(0 2px 4px rgba(0,0,0,0.5))");
  svg.appendChild(c);
  return c;
}

function svgPoint(svg: SVGSVGElement, clientX: number, clientY: number): { x: number; y: number } | null {
  // Use the browser's screen CTM so we respect preserveAspectRatio (which
  // letterboxes/pillarboxes the viewBox inside the SVG element when their
  // aspects differ). A naive clientX/width * VIEW_W mapping ignores those
  // bars and pushes taps near point boundaries onto the wrong column.
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const local = pt.matrixTransform(ctm.inverse());
  return { x: local.x, y: local.y };
}

// A game is under way (as opposed to the idle board or a finished game).
function isInGame(controller: GameController): boolean {
  const phase = controller.state.phase.kind;
  return (
    phase === "play" || phase === "roll" || phase === "opening" || phase === "cpu-thinking" || phase === "cube-decision"
  );
}

function openMenu(controller: GameController, root: HTMLElement): void {
  const node = showMenu(controller.state.settings, {
    onStart: (settings) => {
      controller.setSettings(settings);
      controller.startNewMatch();
      attemptOrientationLock();
    },
  });
  root.appendChild(node);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

// "You wins" → "You win". Second-person pronoun names take a plural verb.
function winnerIsSecondPerson(name: string): boolean {
  return /^you$/i.test(name.trim());
}

function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  // Never in the Android app. Its origin is https://localhost, so the https
  // guard below would pass — and the SW would duplicate the ~9 MB of weights
  // into Cache Storage and re-validate bearoff.bin on every launch, when those
  // files are already local assets inside the APK.
  if (isNativeApp()) return;
  // In dev, never register — and proactively unregister any stale SW from a
  // prior prod-build visit on the same origin, plus wipe its caches. Without
  // this, the SW keeps serving the old built /index.html (which references
  // hashed bundles the dev server doesn't have), so refreshes return blank.
  if (import.meta.env?.DEV) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      for (const r of regs) void r.unregister();
    }).catch(() => undefined);
    if ("caches" in globalThis) {
      caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))).catch(() => undefined);
    }
    return;
  }
  if (location.protocol !== "https:" && location.hostname !== "localhost") return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  });
}

void main();
